import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useCompanies } from './hooks';
import { useCurrentDomain } from "./context/DomainContext";
import { getReferralTemplatesByCompany, assignReferralTemplates, deleteReferralTemplateMapping } from './services/masterDataService';

const ConfigureReferralTemplate = () => {
    const { domain } = useCurrentDomain();
    const [referralTemplates, setReferralTemplates] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
    const [mappedTemplates, setMappedTemplates] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [templateSearch, setTemplateSearch] = useState('');
    const [addLoading, setAddLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    const { companies, loading: companiesLoading } = useCompanies(domain);

    const BRAND_GREEN = '#0aa375';

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '30px',
            fontSize: '12px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: BRAND_GREEN },
        }),
        indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
        dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
        clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
        option: (base, state) => ({
            ...base,
            fontSize: '12px',
            backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
            color: state.isSelected ? 'white' : '#333',
        }),
        singleValue: (base) => ({ ...base, fontSize: '12px' }),
        placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
    };

    const Field = ({ label, required }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                {label}
                {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
            </span>
        </div>
    );
    const Spinner = ({ size = 'sm', color = BRAND_GREEN }) => (
        <div className="text-center py-4">
            <div
                className={`spinner-border spinner-border-${size}`}
                style={{ color }}
                role="status"
            >
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    useEffect(() => {
        if (!selectedCompany) {
            setReferralTemplates([]);
            setMappedTemplates([]);
            setSelectedTemplateIds([]);
            setSelectedRows([]);
            setSelectAll(false);
            return;
        }
        fetchTemplates();
    }, [selectedCompany]);

    const fetchTemplates = async () => {
        setTemplatesLoading(true);
        try {
            const data = await getReferralTemplatesByCompany(selectedCompany.value, domain);
            setReferralTemplates(data.filter(t => !t.isAssigned));
            setMappedTemplates(data.filter(t => t.isAssigned));
            setSelectedTemplateIds([]);
            setSelectedRows([]);
            setSelectAll(false);
        } catch (err) {
            toast.error('Failed to load referral templates.');
        } finally {
            setTemplatesLoading(false);
        }
    };

    const handleAddMapping = async () => {
        if (!selectedCompany || selectedTemplateIds.length === 0) return;
        setAddLoading(true);
        const loadingToast = toast.loading('Assigning referral templates...');
        try {
            await assignReferralTemplates(selectedCompany.value, selectedTemplateIds, domain);
            toast.dismiss(loadingToast);
            toast.success(`${selectedTemplateIds.length} template(s) assigned successfully.`);
            await fetchTemplates();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to assign referral templates.');
        } finally {
            setAddLoading(false);
        }
    };

    const handleDeleteMapping = async () => {
        if (!selectedCompany || selectedRows.length === 0) return;
        const toastId = toast(
            <div style={{ textAlign: 'center', minWidth: '250px' }}>
                <div style={{ fontWeight: '600', marginBottom: '10px' }}>
                    Delete {selectedRows.length} mapping(s)?
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => toast.dismiss(toastId)}>
                        Cancel
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => { toast.dismiss(toastId); performDelete(); }}>
                        Yes, Delete
                    </button>
                </div>
            </div>,
            { autoClose: false, closeButton: false, closeOnClick: false, draggable: false, className: 'bg-white shadow' }
        );
    };

    const performDelete = async () => {
        setDeleteLoading(true);
        const loadingToast = toast.loading('Deleting mappings...');
        try {
            await deleteReferralTemplateMapping(selectedCompany.value, selectedRows, domain);
            toast.dismiss(loadingToast);
            toast.success(`${selectedRows.length} mapping(s) deleted successfully.`);
            await fetchTemplates();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to delete mappings.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCompanyChange = (selected) => {
        setSelectedCompany(selected);
        setSelectedTemplateIds([]);
        setSelectedRows([]);
        setSelectAll(false);
        setTemplateSearch('');
    };

    const filteredTemplates = referralTemplates.filter(t =>
        (t.templateName || '').toLowerCase().includes(templateSearch.toLowerCase())
    );

    const allMappedSelected = mappedTemplates.length > 0 && mappedTemplates.every(t => selectedRows.includes(t.templateId));

    const handleSelectAll = () => {
        if (allMappedSelected) setSelectedRows([]);
        else setSelectedRows(mappedTemplates.map(t => t.templateId));
        setSelectAll(!allMappedSelected);
    };

    return (
        <div className="container-fluid p-0 px-1">
            <div className="card-body">
                <div className="row g-2">
                    <div className="col-12 col-lg-6">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={{ color: '#fff', fontWeight: 600,fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                                Add Templates
                            </div>
                            <div className="card-body p-2">
                                <div className="mb-2">
                                    <Field label="Company" required />
                                    <Select
                                        options={companies}
                                        value={selectedCompany}
                                        onChange={handleCompanyChange}
                                        isLoading={companiesLoading}
                                        placeholder="Select company..."
                                        isSearchable
                                        isClearable
                                        styles={selectStyles}
                                    />
                                </div>
                                <div className="mb-2">
                                    <Field label="Search Templates" />
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Filter template list..."
                                        value={templateSearch}
                                        onChange={e => setTemplateSearch(e.target.value)}
                                        disabled={!selectedCompany}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>
                                <div style={{ height: '280px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                                    {templatesLoading ? (
                                        <Spinner />
                                    ) : filteredTemplates.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            {selectedCompany ? 'No unassigned templates.' : 'Select a company first.'}
                                        </div>
                                    ) : (
                                        filteredTemplates.map(t => (
                                            <label
                                                key={t.templateId}
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12px' }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTemplateIds.includes(t.templateId)}
                                                    onChange={() =>
                                                        setSelectedTemplateIds(prev =>
                                                            prev.includes(t.templateId)
                                                                ? prev.filter(id => id !== t.templateId)
                                                                : [...prev, t.templateId]
                                                        )
                                                    }
                                                    style={{ accentColor: BRAND_GREEN }}
                                                />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {t.templateName}
                                                    </div>
                                                    {t.description && (
                                                        <div style={{ fontSize: '10px', color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {t.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>
                                <div className="d-flex justify-content-center gap-2 mt-2">
                                    <button
                                        onClick={() => { setSelectedTemplateIds([]); setTemplateSearch(''); }}
                                        className="btn btn-outline-secondary btn-sm px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={handleAddMapping}
                                        disabled={addLoading || !selectedCompany || selectedTemplateIds.length === 0}
                                        className="btn btn-sm text-white px-3 fw-semibold"
                                        style={{ backgroundColor: BRAND_GREEN, border: 'none', fontSize: '12px', opacity: (addLoading || !selectedCompany || selectedTemplateIds.length === 0) ? 0.65 : 1 }}
                                    >
                                        {addLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Saving...
                                            </>
                                        ) : (
                                            `Add Mapping (${selectedTemplateIds.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-6">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={{ color: '#fff', fontWeight: 600,fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                                Mapped Templates
                            </div>
                            <div className="card-body p-2">
                                {mappedTemplates.length > 0 && (
                                    <div className="d-flex align-items-center justify-content-between mb-1 px-1 py-1"
                                        style={{ borderBottom: '1px solid #e9ecef', minHeight: '28px' }}>

                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="selectAllMapped"
                                                checked={allMappedSelected}
                                                onChange={handleSelectAll}
                                                disabled={mappedTemplates.length === 0}
                                                style={{ accentColor: BRAND_GREEN, width: '14px', height: '14px', cursor: 'pointer' }}
                                            />
                                            <label className="form-check-label mb-0" style={{ fontSize: '11px', fontWeight: 600, color: '#495057', cursor: 'pointer', userSelect: 'none' }} htmlFor="selectAllMapped">
                                                Select All
                                            </label>
                                            {mappedTemplates.length > 0 && (
                                                <span style={{ fontSize: '10px', color: '#adb5bd' }}>
                                                    ({mappedTemplates.length} total)
                                                </span>
                                            )}
                                        </div>

                                        {selectedRows.length > 0 && (
                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                background: '#e6f7f2',
                                                color: BRAND_GREEN,
                                                padding: '2px 8px',
                                                borderRadius: '20px',
                                                border: `1px solid ${BRAND_GREEN}22`
                                            }}>
                                                ✓ {selectedRows.length} selected
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    {templatesLoading ? (
                                        <Spinner />
                                    ) : mappedTemplates.length === 0 ? (
                                        <div className="alert alert-success small py-1 mb-0 m-2">No Active Mappings.</div>
                                    ) : (
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: '36px', padding: '6px 10px' }}></th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Template Name</th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mappedTemplates.map(item => (
                                                    <tr key={item.templateId} style={{ background: selectedRows.includes(item.templateId) ? '#e6f7f2' : 'white' }}>
                                                        <td style={{ padding: '6px 10px' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedRows.includes(item.templateId)}
                                                                onChange={() =>
                                                                    setSelectedRows(prev =>
                                                                        prev.includes(item.templateId)
                                                                            ? prev.filter(id => id !== item.templateId)
                                                                            : [...prev, item.templateId]
                                                                    )
                                                                }
                                                                style={{ accentColor: BRAND_GREEN }}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '6px 10px', fontWeight: 500 }}>{item.templateName}</td>
                                                        <td style={{ padding: '6px 10px', color: '#6c757d', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {item.description || '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="d-flex justify-content-center mt-2">
                                    <button
                                        onClick={handleDeleteMapping}
                                        disabled={deleteLoading || selectedRows.length === 0}
                                        className="btn btn-sm btn-danger px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {deleteLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Deleting...
                                            </>
                                        ) : (
                                            `Delete Selected (${selectedRows.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfigureReferralTemplate;